import { useState, useEffect, useRef } from 'react'
import { Users, Crown, Activity, Radio, TrendingUp, DollarSign, Volume2, VolumeX, Loader2, Zap } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useUserStats } from '@/lib/data'
import { UserGrowthChart } from '../Charts'

interface UserGraphViewProps {
  client: SupabaseClient | null
}

export function UserGraphView({ client }: UserGraphViewProps) {
  // Debug logging
  console.log('UserGraphView - client:', client ? 'CONNECTED' : 'NULL')

  const {
    totalUsers,
    premiumUsers,
    premiumMonthly,
    premiumYearly,
    yearlyThisMonth,
    monthlyThisMonth,
    yearlyRenewalsThisMonth,
    avgMonthlyAcquisition,
    avgYearlyAcquisition,
    monthsOfData,
    monthProgress,
    recentUsers,
    userGrowthData,
    events,
    isConnected,
    connectionError,
    eventCounter,
  } = useUserStats(client)

  const [timePeriod, setTimePeriod] = useState<'10' | '30' | '60' | '1440'>('60')
  const [isMuted, setIsMuted] = useState(true)
  const previousEventCountRef = useRef(0)

  // Get users count for selected time period
  const usersInPeriod = {
    '10': recentUsers.last10Min,
    '30': recentUsers.last30Min,
    '60': recentUsers.last1Hour,
    '1440': recentUsers.last24Hours,
  }[timePeriod]

  // Play sounds when events occur (simplified - no actual sound for now)
  useEffect(() => {
    if (eventCounter > previousEventCountRef.current && !isMuted) {
      // Sound effects would go here
      console.log('New event detected!')
    }
    previousEventCountRef.current = eventCounter
  }, [eventCounter, isMuted])

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <TrendingUp size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to view user statistics</p>
      </div>
    )
  }

  const freeUsers = totalUsers - premiumUsers
  const conversionRate = totalUsers > 0 ? ((premiumUsers / totalUsers) * 100).toFixed(1) : '0.0'

  // Financial calculations
  const MONTHLY_PRICE = 5
  const YEARLY_PRICE = 50
  const PLATFORM_CUT = 0.15 // Apple/Google take 15%

  // MRR: Monthly subscribers × $5 + Yearly subscribers × ($50/12)
  const grossMRR = (premiumMonthly * MONTHLY_PRICE) + (premiumYearly * (YEARLY_PRICE / 12))
  const netMRR = grossMRR * (1 - PLATFORM_CUT)

  // ARR: Monthly subscribers × $5 × 12 + Yearly subscribers × $50
  const grossARR = (premiumMonthly * MONTHLY_PRICE * 12) + (premiumYearly * YEARLY_PRICE)
  const netARR = grossARR * (1 - PLATFORM_CUT)

  // === THIS MONTH'S CASH BREAKDOWN ===
  const currentMonth = new Date().toLocaleString('en-US', { month: 'long' })

  // NEW subscriber revenue (first-time payments this month)
  const newMonthlyRevenue = monthlyThisMonth * MONTHLY_PRICE
  const newYearlyRevenue = yearlyThisMonth * YEARLY_PRICE
  const newSubscriberRevenue = newMonthlyRevenue + newYearlyRevenue

  // RECURRING revenue (existing subs paying again)
  const recurringMonthlyRevenue = (premiumMonthly - monthlyThisMonth) * MONTHLY_PRICE
  const recurringYearlyRevenue = yearlyRenewalsThisMonth * YEARLY_PRICE
  const recurringRevenue = recurringMonthlyRevenue + recurringYearlyRevenue

  // TOTAL cash collected this month
  const totalCashThisMonth = newSubscriberRevenue + recurringRevenue
  const netCashThisMonth = totalCashThisMonth * (1 - PLATFORM_CUT)

  // Projected ARR Growth
  const projectedMonthlyIn12Mo = premiumMonthly + (avgMonthlyAcquisition * 12)
  const projectedYearlyIn12Mo = premiumYearly + (avgYearlyAcquisition * 12)
  const projectedGrossARR12Mo = (projectedMonthlyIn12Mo * MONTHLY_PRICE * 12) + (projectedYearlyIn12Mo * YEARLY_PRICE)
  const projectedNetARR12Mo = projectedGrossARR12Mo * (1 - PLATFORM_CUT)

  const projectedMonthlyIn6Mo = premiumMonthly + (avgMonthlyAcquisition * 6)
  const projectedYearlyIn6Mo = premiumYearly + (avgYearlyAcquisition * 6)
  const projectedGrossARR6Mo = (projectedMonthlyIn6Mo * MONTHLY_PRICE * 12) + (projectedYearlyIn6Mo * YEARLY_PRICE)
  const projectedNetARR6Mo = projectedGrossARR6Mo * (1 - PLATFORM_CUT)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <Radio className="h-4 w-4 text-emerald-400 animate-pulse" />
                <span className="text-sm font-medium text-emerald-400">Live</span>
              </>
            ) : (
              <>
                <Radio className="h-4 w-4 text-[var(--text-muted)]" />
                <span className="text-sm font-medium text-[var(--text-muted)]">Connecting...</span>
              </>
            )}
          </div>

          {connectionError && (
            <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400">
              {connectionError}
            </span>
          )}
        </div>

        <button
          onClick={() => setIsMuted(!isMuted)}
          className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-element)] text-[var(--text-muted)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors"
        >
          {isMuted ? (
            <>
              <VolumeX className="h-4 w-4" />
              Unmute
            </>
          ) : (
            <>
              <Volume2 className="h-4 w-4" />
              Mute
            </>
          )}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <StatCard icon={Users} label="Total Users" value={totalUsers.toLocaleString()} subtext="All registered accounts" />
        <StatCard icon={Crown} label="Premium Users" value={premiumUsers.toString()} subtext={`${conversionRate}% conversion`} highlight />
        <StatCard icon={Users} label="Free Users" value={freeUsers.toString()} subtext={`${totalUsers > 0 ? ((freeUsers / totalUsers) * 100).toFixed(1) : 0}% of total`} />
        <StatCard icon={Activity} label="Live Events" value={events.length.toString()} subtext="Recent activity" highlight />
        <div className="bg-[var(--bg-card)] border border-[var(--border)] p-4 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="h-5 w-5 text-[var(--primary)]" />
            <span className="text-[var(--text-muted)] text-sm">New Users</span>
          </div>
          <p className="text-2xl font-bold text-[var(--primary)]">{usersInPeriod}</p>
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value as any)}
            className="mt-2 w-full text-xs bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-muted)] rounded px-2 py-1"
          >
            <option value="10">Last 10 minutes</option>
            <option value="30">Last 30 minutes</option>
            <option value="60">Last 1 hour</option>
            <option value="1440">Last 24 hours</option>
          </select>
        </div>
        <StatCard icon={DollarSign} label="MRR" value={`$${netMRR.toFixed(0)}`} subtext={`$${grossMRR.toFixed(0)} gross`} color="green" />
      </div>

      {/* User & Revenue Breakdown */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* User Breakdown */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-base font-semibold text-[var(--text-main)] mb-4 flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-400" />
            User Breakdown
          </h3>
          <div className="space-y-4">
            {/* Premium Users - Monthly & Yearly */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="text-xs text-amber-400 mb-1">Monthly</div>
                <div className="text-xl font-bold text-[var(--text-main)]">{premiumMonthly}</div>
                <div className="text-xs text-[var(--text-muted)]">${MONTHLY_PRICE}/mo each</div>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="text-xs text-amber-400 mb-1">Yearly</div>
                <div className="text-xl font-bold text-[var(--text-main)]">{premiumYearly}</div>
                <div className="text-xs text-[var(--text-muted)]">${YEARLY_PRICE}/yr each</div>
              </div>
            </div>

            {/* Total Premium */}
            <div className="flex items-center justify-between p-2 rounded bg-amber-500/10">
              <span className="text-xs font-medium text-[var(--text-muted)]">Total Premium</span>
              <span className="text-sm font-bold text-amber-400">{premiumUsers}</span>
            </div>

            {/* Conversion Rate */}
            <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
              <span className="text-xs text-[var(--text-muted)]">Conversion Rate</span>
              <span className="text-sm font-bold text-[var(--primary)]">{conversionRate}%</span>
            </div>
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-base font-semibold text-[var(--text-main)] mb-4 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-400" />
            Revenue
          </h3>
          <div className="space-y-4">
            {/* MRR & ARR Summary */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="text-xs text-emerald-400 mb-1">MRR (Net)</div>
                <div className="text-xl font-bold text-emerald-400">${netMRR.toFixed(0)}</div>
                <div className="text-xs text-[var(--text-muted)]">${grossMRR.toFixed(0)} gross</div>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="text-xs text-emerald-400 mb-1">ARR (Net)</div>
                <div className="text-xl font-bold text-emerald-400">${netARR.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                <div className="text-xs text-[var(--text-muted)]">${grossARR.toLocaleString()} gross</div>
              </div>
            </div>

            {/* Revenue by Plan */}
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 rounded bg-[var(--bg-element)]">
                <div className="text-xs">
                  <span className="text-[var(--text-main)]">Monthly @ $5</span>
                  <span className="text-[var(--text-muted)] ml-1">({premiumMonthly})</span>
                </div>
                <span className="text-sm font-semibold text-[var(--text-main)]">${(premiumMonthly * MONTHLY_PRICE).toLocaleString()}/mo</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-[var(--bg-element)]">
                <div className="text-xs">
                  <span className="text-[var(--text-main)]">Yearly @ $50</span>
                  <span className="text-[var(--text-muted)] ml-1">({premiumYearly})</span>
                </div>
                <span className="text-sm font-semibold text-[var(--text-main)]">${(premiumYearly * YEARLY_PRICE).toLocaleString()}/yr</span>
              </div>
            </div>

            {/* This Month's Cash Breakdown */}
            <div className="space-y-2 pt-2 border-t border-[var(--border)]">
              <div className="text-xs font-medium text-[var(--text-muted)] mb-2">{currentMonth} Cash Collected</div>

              {/* New Subscriber Revenue */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-muted)]">New Subscribers</span>
                <span className="font-semibold text-emerald-400">+${newSubscriberRevenue.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs pl-3 text-[var(--text-muted)] opacity-70">
                <span>{monthlyThisMonth} monthly × $5</span>
                <span>${newMonthlyRevenue.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs pl-3 text-[var(--text-muted)] opacity-70">
                <span>{yearlyThisMonth} yearly × $50</span>
                <span>${newYearlyRevenue.toLocaleString()}</span>
              </div>

              {/* Recurring Revenue */}
              <div className="flex items-center justify-between text-xs mt-2">
                <span className="text-[var(--text-muted)]">Recurring</span>
                <span className="font-semibold text-blue-400">+${recurringRevenue.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs pl-3 text-[var(--text-muted)] opacity-70">
                <span>{premiumMonthly - monthlyThisMonth} monthly renewals</span>
                <span>${recurringMonthlyRevenue.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs pl-3 text-[var(--text-muted)] opacity-70">
                <span>{yearlyRenewalsThisMonth} yearly renewals</span>
                <span>${recurringYearlyRevenue.toLocaleString()}</span>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
                <span className="text-sm font-medium text-[var(--text-main)]">Total Gross</span>
                <span className="text-sm font-bold text-emerald-400">${totalCashThisMonth.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[var(--primary)]">Net Payout</span>
                <span className="text-sm font-bold text-[var(--primary)]">${netCashThisMonth.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Projected ARR Growth */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-400" />
            <div>
              <h3 className="text-base font-semibold text-[var(--text-main)]">Projected ARR Growth</h3>
              <p className="text-sm text-[var(--text-muted)]">
                Based on {currentMonth} growth ({monthProgress?.percentComplete || 0}% complete) → ~{avgMonthlyAcquisition} monthly + {avgYearlyAcquisition} yearly/mo
              </p>
            </div>
          </div>
          {monthProgress && (
            <div className="text-right">
              <div className="text-xs text-[var(--text-muted)]">Month Progress</div>
              <div className="text-sm font-semibold text-[var(--primary)]">
                Day {monthProgress.daysIntoMonth} of {monthProgress.daysInMonth}
              </div>
            </div>
          )}
        </div>

        {/* Current + Next 3 Months Projection */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          {/* Current Month */}
          <div className="p-4 rounded-lg bg-[var(--primary)]/10 border border-[var(--primary)]/30">
            <div className="text-xs text-[var(--primary)] mb-1">{currentMonth} (Current)</div>
            <div className="text-lg font-bold text-[var(--text-main)]">+{monthlyThisMonth + yearlyThisMonth}</div>
            <div className="text-xs text-[var(--text-muted)]">
              {monthlyThisMonth} mo · {yearlyThisMonth} yr
            </div>
            <div className="mt-2 h-1 bg-[var(--bg-element)] rounded-full overflow-hidden">
              <div
                className="h-full bg-[var(--primary)] rounded-full transition-all"
                style={{ width: `${monthProgress?.percentComplete || 0}%` }}
              />
            </div>
          </div>

          {/* Next 3 months */}
          {[1, 2, 3].map((offset) => {
            const monthDate = new Date()
            monthDate.setMonth(monthDate.getMonth() + offset)
            const monthName = monthDate.toLocaleString('en-US', { month: 'short' })
            const projectedMonthly = Math.round(avgMonthlyAcquisition)
            const projectedYearly = Math.round(avgYearlyAcquisition)
            const cumulativeMonthly = premiumMonthly + (avgMonthlyAcquisition * offset)
            const cumulativeYearly = premiumYearly + (avgYearlyAcquisition * offset)
            const projectedARR = ((cumulativeMonthly * MONTHLY_PRICE * 12) + (cumulativeYearly * YEARLY_PRICE)) * (1 - PLATFORM_CUT)

            return (
              <div key={offset} className="p-4 rounded-lg bg-[var(--bg-element)] border border-[var(--border)]">
                <div className="text-xs text-[var(--text-muted)] mb-1">{monthName}</div>
                <div className="text-lg font-bold text-blue-400">+{projectedMonthly + projectedYearly}</div>
                <div className="text-xs text-[var(--text-muted)]">
                  {projectedMonthly} mo · {projectedYearly} yr
                </div>
                <div className="text-xs text-emerald-400 mt-2">
                  ${projectedARR.toLocaleString(undefined, { maximumFractionDigits: 0 })} ARR
                </div>
              </div>
            )
          })}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          {/* Current ARR */}
          <div className="p-4 rounded-lg bg-[var(--bg-element)] border border-[var(--border)]">
            <div className="text-xs text-[var(--text-muted)] mb-1">Current ARR</div>
            <div className="text-2xl font-bold text-[var(--text-main)]">${netARR.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-xs text-[var(--text-muted)] mt-1">
              {premiumMonthly} monthly + {premiumYearly} yearly
            </div>
          </div>

          {/* 6 Month Projection */}
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <div className="text-xs text-blue-400 mb-1">6 Month Projection</div>
            <div className="text-2xl font-bold text-blue-400">${projectedNetARR6Mo.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-xs text-blue-300 mt-1">
              +${(projectedNetARR6Mo - netARR).toLocaleString(undefined, { maximumFractionDigits: 0 })} ({netARR > 0 ? ((projectedNetARR6Mo / netARR - 1) * 100).toFixed(0) : 0}%)
            </div>
          </div>

          {/* 12 Month Projection */}
          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <div className="text-xs text-emerald-400 mb-1">12 Month Projection</div>
            <div className="text-2xl font-bold text-emerald-400">${projectedNetARR12Mo.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            <div className="text-xs text-emerald-300 mt-1">
              +${(projectedNetARR12Mo - netARR).toLocaleString(undefined, { maximumFractionDigits: 0 })} ({netARR > 0 ? ((projectedNetARR12Mo / netARR - 1) * 100).toFixed(0) : 0}%)
            </div>
          </div>
        </div>

        {/* Rate Details */}
        <div className="mt-4 pt-4 border-t border-[var(--border)]">
          <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
            <span>Projected monthly rate: <span className="text-blue-400 font-semibold">+{avgMonthlyAcquisition} monthly subs</span> + <span className="text-emerald-400 font-semibold">+{avgYearlyAcquisition} yearly subs</span></span>
            <span>= <span className="text-[var(--text-main)] font-semibold">+${((avgMonthlyAcquisition * MONTHLY_PRICE * 12) + (avgYearlyAcquisition * YEARLY_PRICE)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> ARR/month</span>
          </div>
        </div>
      </div>

      {/* User Growth Chart */}
      {userGrowthData.length > 0 && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-[var(--primary)]" />
            <div>
              <h3 className="text-base font-semibold text-[var(--text-main)]">User Growth</h3>
              <p className="text-sm text-[var(--text-muted)]">Total users over the last 30 days</p>
            </div>
          </div>
          <div className="h-[250px]">
            <UserGrowthChart data={userGrowthData} />
          </div>
        </div>
      )}

      {/* Live Event Feed */}
      {events.length > 0 && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-5 w-5 text-[var(--primary)]" />
            <div>
              <h3 className="text-base font-semibold text-[var(--text-main)]">Live Event Feed</h3>
              <p className="text-sm text-[var(--text-muted)]">Most recent user activity ({events.length} events)</p>
            </div>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {events.map((event, index) => (
              <div
                key={`${event.id}-${index}`}
                className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-300 ${
                  index === 0 ? 'bg-[var(--primary)]/5 border-[var(--primary)]/20' : 'bg-[var(--bg-element)] border-[var(--border)]'
                }`}
              >
                <div className="flex items-center gap-3">
                  {event.type === 'new_user' ? (
                    <div className="w-8 h-8 bg-[var(--primary)]/20 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-[var(--primary)]" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center">
                      <Crown className="h-4 w-4 text-amber-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-[var(--text-main)]">
                      {event.type === 'new_user'
                        ? 'New user registered'
                        : `Premium upgrade${event.billingPeriod ? ` (${event.billingPeriod})` : ''}`}
                    </p>
                    <p className="text-xs text-[var(--text-muted)]">
                      {event.name || event.email || 'Anonymous'} • {new Date(event.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  event.type === 'new_user'
                    ? 'bg-[var(--primary)]/20 text-[var(--primary)]'
                    : event.billingPeriod === 'yearly'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-amber-500/10 text-amber-400'
                }`}>
                  {event.type === 'new_user'
                    ? 'New'
                    : event.billingPeriod === 'yearly'
                      ? 'Yearly'
                      : event.billingPeriod === 'monthly'
                        ? 'Monthly'
                        : 'Premium'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-blue-400">Real-time Updates Active</p>
            <p className="text-xs text-blue-300">
              This page shows live user registrations and premium upgrades. Data refreshes every 2 seconds.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  icon: React.ElementType
  label: string
  value: string
  subtext: string
  highlight?: boolean
  color?: 'default' | 'green'
}

function StatCard({ icon: Icon, label, value, subtext, highlight, color = 'default' }: StatCardProps) {
  return (
    <div className={`bg-[var(--bg-card)] border p-4 rounded-xl ${
      highlight ? 'border-amber-500/20' : color === 'green' ? 'border-emerald-500/20' : 'border-[var(--border)]'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={`h-5 w-5 ${
          highlight ? 'text-amber-400' : color === 'green' ? 'text-emerald-400' : 'text-[var(--primary)]'
        }`} />
        <span className="text-[var(--text-muted)] text-sm">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${
        highlight ? 'text-amber-400' : color === 'green' ? 'text-emerald-400' : 'text-[var(--text-main)]'
      }`}>
        {value}
      </p>
      <p className="text-[var(--text-muted)] text-xs mt-1">{subtext}</p>
    </div>
  )
}
